import {
  IS_OUT_OF_STOCK,
  VARIANT_ATTRIBUTE_NAME_COLOR_CODE,
} from '../common/constants';
import {
  ORDERED_SIZE_ATTRIBUTES,
  getEquivalentSizeKeys,
  normalizeSizeKey,
} from '../sizes';

const findSizeAttribute = (attributes = []) =>
  attributes.find(({ name }) => ORDERED_SIZE_ATTRIBUTES.includes(name));

/**
 * Finds the variant among `variants` whose own size matches `sourceAttributes`'
 * size attribute, translating across product types (e.g. a pillow's
 * "standard" size to a sheet set's "queen") via getEquivalentSizeKeys. This is
 * what lets an AOV "you might also need" recommendation -- whether it's the
 * flat-rate flyout (AOVVerticalCards) or the buy-more-save-more one
 * (BuyMoreSaveMore) -- default to a size that actually fits what the shopper
 * just added, instead of whatever variant happens to be listed first for
 * that addon. See CSE-1073.
 *
 * Returns `undefined` when the source has no recognisable size attribute, or
 * none of `variants` matches any equivalent key -- callers should keep their
 * own fallback (e.g. the addon's first variant) for that case, same as
 * before this match existed.
 */
export const findSizeMatchedVariant = (variants = [], sourceAttributes = []) => {
  const sourceSizeAttribute = findSizeAttribute(sourceAttributes);
  if (!sourceSizeAttribute) return undefined;

  const equivalentSizeKeys = getEquivalentSizeKeys(sourceSizeAttribute.key);

  return equivalentSizeKeys
    .map((equivalentKey) =>
      variants.find(
        (candidate) =>
          normalizeSizeKey(findSizeAttribute(candidate.attributes)?.key) ===
          equivalentKey,
      ),
    )
    .find(Boolean);
};

export const formatProductSummaryName = (productName, attributes) =>
  productName +
  attributes.reduce(
    (acc, curr) =>
      acc +
      (curr.name !== VARIANT_ATTRIBUTE_NAME_COLOR_CODE
        ? `, ${curr.label}`
        : ''),
    '',
  );

export const findDiscountTier = (discountsTier, productQuantity) =>
  discountsTier?.find(({ productCount }) => productCount === productQuantity);

export const findOutOfStock = (attributes) =>
  !!attributes.find(
    (attr) => attr.name === IS_OUT_OF_STOCK && attr.label === 'true',
  );

export const findProductInOptionsProductList = (
  optionsProductList,
  productId,
) => {
  const optionFound = optionsProductList.find(
    ({ gift, enhancedProduct }) =>
      gift.productId === productId || enhancedProduct.productId === productId,
  );

  return optionFound?.gift.productId === productId
    ? optionFound.gift
    : optionFound?.enhancedProduct;
};

export const findProductInCompleteSetProductsList = (
  completeSetProductList,
  productId,
) =>
  completeSetProductList?.find(
    ({ productId: productIdOrigin }) => productIdOrigin === productId,
  );

export const deleteProductFromProductsSaved = ({ state, productId }) => {
  const { [productId]: removed, ...newState } = state;

  return newState;
};

export const addProductInProductsSaved = ({ state, productId, sku }) => ({
  ...state,
  [productId]: [sku],
});

export const sumPrices = (accPrice, currPrice) => ({
  price: {
    ...currPrice.price,
    centAmount: accPrice.price.centAmount + currPrice.price.centAmount,
  },
  discount: {
    ...currPrice.price,
    centAmount:
      (accPrice.discount?.centAmount ?? 0) +
      (currPrice.discount?.centAmount ?? 0),
  },
});
